// CLIENT LIST — every client with since when and where they came from (source, referred by).
// Print (A4, branded), CSV for Excel, and fill in missing sources and dates right in the list.
import { useMemo, useState } from 'react';
import {
  CLIENT_SOURCES, clientRegister, customSources, sourceInfo, clientSinceOf, toCsv, updatePerson,
  type ClientRegister, type PersonStage, type RegisterRow, type SourceInfo,
} from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Empty, Field, PageHead, Seg } from '../core/ui.tsx';
import { DateField } from '../core/date-field.tsx';
import { Icon } from '../core/icons.tsx';
import { download } from '../core/links.ts';
import { navigate, personPath } from '../core/router.ts';
import { printClientRegister } from '../core/print.ts';

type Ev = { target: HTMLInputElement | HTMLSelectElement };

/** label of a source as the advisor reads it: the standard name, or their own text as typed */
export function sourceText(rt: Pick<Runtime, 't'>, info: SourceInfo): string {
  return info.key === 'custom' ? info.text ?? '' : rt.t(`growth_a.source.${info.key}`);
}
const groupLabel = (rt: Pick<Runtime, 't'>, g: { key: SourceInfo['key']; text: string | null }) => sourceText(rt, { key: g.key, text: g.text, group: '' });

/** Source picker: standard sources, the advisor's own sources already in use, or a new one typed in.
 *  `value` is what is stored on the person (a standard key, or the advisor's text). */
export function SourcePicker({ value, onChange, testId, label }: { value: string; onChange: (v: string) => void; testId?: string; label?: string }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const own = useMemo(() => customSources(view), [view]);
  const info = sourceInfo(value);
  const [typing, setTyping] = useState(false);
  const current = typing ? '__new' : info.key === 'unknown' ? '' : info.key === 'custom' ? `custom:${info.text}` : info.key;
  const owns = info.key === 'custom' && !own.includes(info.text!) ? [info.text!, ...own] : own;
  const pick = (x: string) => {
    if (x === '__new') { setTyping(true); onChange(''); return; }
    setTyping(false);
    onChange(x.startsWith('custom:') ? x.slice(7) : x);
  };
  return (
    <div className="source-picker">
      <select className="select" value={current} onChange={(e: Ev) => pick(e.target.value)} data-testid={testId} aria-label={label ?? t('person.source')}>
        <option value="">{t('growth_a.source.unknown')}</option>
        {CLIENT_SOURCES.map((k) => <option key={k} value={k}>{t(`growth_a.source.${k}`)}</option>)}
        {owns.length > 0 && <optgroup label={t('person.source_own')}>{owns.map((x) => <option key={x} value={`custom:${x}`}>{x}</option>)}</optgroup>}
        <option value="__new">{t('person.source_other')}</option>
      </select>
      {typing && <input className="input" autoFocus value={info.key === 'custom' ? info.text ?? '' : ''} placeholder={t('person.source_own_ph')} aria-label={t('person.source_own')}
        onChange={(e: Ev) => onChange(e.target.value)} data-testid={testId ? `${testId}-own` : undefined} />}
    </div>
  );
}

/** "Referred by": an existing person (picked from the list) or just a name. */
export function ReferredByInput({ value, onChange, selfId, testId, onBlur }: { value: string; onChange: (v: string) => void; selfId?: string; testId?: string; onBlur?: () => void }) {
  const rt = useRuntime();
  const names = useMemo(() => rt.view.rows('persons').filter((p) => p.id !== selfId && !p.archivedAt && p.stage !== 'dependent').map((p) => p.displayName).sort(rt.i18n.compare), [rt.view, selfId, rt.i18n]);
  const list = `vero-referrers-${selfId ?? 'new'}`;
  return (<>
    <input className="input" list={list} value={value} onChange={(e: Ev) => onChange(e.target.value)} onBlur={onBlur} placeholder={rt.t('person.referred_by_hint')} aria-label={rt.t('person.referred_by')} data-testid={testId} />
    <datalist id={list}>{names.map((n) => <option key={n} value={n} />)}</datalist>
  </>);
}
/** the typed name → a link to that person when exactly one active person has that name */
export function resolveReferrer(rt: Pick<Runtime, 'view'>, name: string, selfId?: string): { referredByPersonId: string | null; referredByName: string | null } {
  const n = name.trim();
  if (!n) return { referredByPersonId: null, referredByName: null };
  const hits = rt.view.rows('persons').filter((p) => p.id !== selfId && !p.archivedAt && p.displayName.trim().toLowerCase() === n.toLowerCase());
  return { referredByPersonId: hits.length === 1 ? hits[0]!.id : null, referredByName: n };
}

type Who = 'clients' | 'with_former' | 'all';
const STAGES: Record<Who, PersonStage[]> = { clients: ['client'], with_former: ['client', 'former'], all: ['client', 'former', 'prospect', 'lead'] };
export interface RegisterPrintOptions { group: boolean; contact: boolean; portfolio: boolean; showStage: boolean; filters: string[] }

export function ClientRegisterView() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const [who, setWho] = useState<Who>('clients');
  const [source, setSource] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<'name' | 'since' | 'source'>('name');
  const [group, setGroup] = useState(true);
  const [contact, setContact] = useState(true);
  const [portfolio, setPortfolio] = useState(false);
  const [fill, setFill] = useState(false);
  const base = useMemo(() => clientRegister(view, today, { stages: STAGES[who], compare: i18n.compare }), [view, today, who, i18n]);
  const reg: ClientRegister = useMemo(() => clientRegister(view, today, { stages: STAGES[who], source, from: from || null, to: to || null, sort, compare: i18n.compare }), [view, today, who, source, from, to, sort, i18n]);
  const filters = [
    t(`register.who_${who}`),
    source ? groupLabel(rt, base.bySource.find((g) => g.group === source) ?? { key: 'unknown', text: null }) : '',
    from || to ? `${t('register.col_since')}: ${from ? i18n.date(from) : '…'} – ${to ? i18n.date(to) : '…'}` : '',
  ].filter(Boolean);
  const opts: RegisterPrintOptions = { group: group && !source, contact, portfolio, showStage: who !== 'clients', filters };
  const exportCsv = () => {
    const head = ['col_no', 'col_name', 'col_stage', 'col_phone', 'col_email', 'col_since', '', 'col_source', 'col_referred', 'col_policies', 'col_premium'].map((k) => (k ? t(`register.${k}`) : t('register.basis')));
    const rows = reg.rows.map((r, i) => [i + 1, r.name, i18n.enumLabel('stage', r.stage), r.phone ?? '', r.email ?? '', r.since ?? '', r.basis ? t(`person.since_basis.${r.basis}`) : '', sourceText(rt, r.source), r.referredBy ?? '', r.activePolicies, r.annualPremium]);
    download(`vero-clients-${today}.csv`, '﻿' + toCsv([head, ...rows]), 'text/csv;charset=utf-8');
    rt.toast(t('register.exported'), 'success');
  };
  const setRow = async (r: RegisterRow, patch: Record<string, unknown>) => { await rt.run((v, c) => updatePerson(v, r.id, patch, c)); };
  const sources = base.bySource;
  return (
    <div data-testid="client-register">
      <PageHead title={t('register.title')} subtitle={t('register.subtitle')}
        actions={<>
          <button className="btn" onClick={() => setFill(!fill)} aria-pressed={fill} data-testid="register-fill"><Icon name={fill ? 'check' : 'note'} />{t(fill ? 'register.fill_done' : 'register.fill')}</button>
          <button className="btn" onClick={exportCsv} disabled={!reg.rows.length} data-testid="register-csv"><Icon name="download" />{t('register.csv')}</button>
          <button className="btn btn-primary" onClick={() => printClientRegister(rt, reg, opts)} disabled={!reg.rows.length} data-testid="register-print"><Icon name="print" />{t('register.print')}</button>
        </>} />
      <section className="card card-pad register-filters">
        <Field label={t('register.who')} group><Seg values={['clients', 'with_former', 'all'] as const} value={who} onChange={setWho} label={(x) => t(`register.who_${x}`)} testId="register-who" /></Field>
        <Field label={t('person.source')}>
          <select className="select" value={source} onChange={(e: Ev) => setSource(e.target.value)} data-testid="register-source">
            <option value="">{t('register.source_all')}</option>
            {sources.map((g) => <option key={g.group} value={g.group}>{groupLabel(rt, g)} ({g.count})</option>)}
          </select>
        </Field>
        <Field label={t('register.from')} group><DateField value={from} max={today} onChange={(d) => setFrom(d)} testId="register-from" /></Field>
        <Field label={t('register.to')} group><DateField value={to} max={today} onChange={(d) => setTo(d)} testId="register-to" /></Field>
        <Field label={t('register.sort')} group><Seg values={['name', 'since', 'source'] as const} value={sort} onChange={setSort} label={(x) => t(`register.sort_${x}`)} testId="register-sort" /></Field>
        <div className="register-checks">
          <label className="check"><input type="checkbox" checked={group} onChange={(e: Ev) => setGroup((e.target as HTMLInputElement).checked)} disabled={!!source} />{t('register.group')}</label>
          <label className="check"><input type="checkbox" checked={contact} onChange={(e: Ev) => setContact((e.target as HTMLInputElement).checked)} />{t('register.contact')}</label>
          <label className="check"><input type="checkbox" checked={portfolio} onChange={(e: Ev) => setPortfolio((e.target as HTMLInputElement).checked)} />{t('register.portfolio')}</label>
        </div>
      </section>

      <div className="register-summary">
        <section className="card card-pad" data-testid="register-by-source">
          <div className="section-title">{t('register.by_source')} · {t('register.count', { count: reg.rows.length })}</div>
          {reg.bySource.length ? <div className="hbars">{reg.bySource.map((g) => (
            <button key={g.group} className="hbar-row" onClick={() => setSource(source === g.group ? '' : g.group)} aria-pressed={source === g.group} title={`${g.count} · ${i18n.number(g.share)}%`}>
              <span className="one-line">{groupLabel(rt, g)}</span><i><b style={{ width: `${Math.max(2, g.share)}%` }} /></i><span className="num">{g.count}</span>
            </button>))}</div> : <p className="small muted">{t('register.empty')}</p>}
        </section>
        <section className="card card-pad" data-testid="register-by-year">
          <div className="section-title">{t('register.by_year')}</div>
          {reg.byYear.length ? <div className="year-cols">{reg.byYear.map((y) => <div key={y.year}><b>{y.count}</b><span>{y.year}</span></div>)}</div> : <p className="small muted">—</p>}
        </section>
      </div>
      {(reg.missing.source > 0 || reg.missing.since > 0) && <p className="small muted register-missing" data-testid="register-missing">{t('register.missing', { source: reg.missing.source, since: reg.missing.since })}</p>}

      <div className="card" style={{ overflow: 'auto' }}>
        {reg.rows.length ? (
          <table className="table register-table" data-testid="register-table">
            <thead><tr>
              <th className="num">{t('register.col_no')}</th><th>{t('register.col_name')}</th>
              {opts.showStage && <th>{t('register.col_stage')}</th>}
              {contact && <th>{t('register.col_phone')}</th>}
              <th>{t('register.col_since')}</th><th>{t('register.col_source')}</th><th>{t('register.col_referred')}</th>
              {portfolio && <><th className="num">{t('register.col_policies')}</th><th className="num">{t('register.col_premium')}</th></>}
            </tr></thead>
            <tbody>{reg.rows.map((r, i) => (
              <tr key={r.id} data-testid="register-row">
                <td className="num muted">{i + 1}</td>
                <td><button className="linkish" onClick={() => navigate(personPath(r.id))}>{r.name}</button>{contact && r.email ? <div className="tiny muted">{r.email}</div> : null}</td>
                {opts.showStage && <td className="small">{i18n.enumLabel('stage', r.stage)}</td>}
                {contact && <td className="small">{r.phone ?? '—'}</td>}
                <td className="small">{fill
                  ? <><DateField size="sm" max={today} value={view.get('persons', r.id)?.clientSince ?? ''} label={`${t('register.col_since')} · ${r.name}`} testId="register-since-input" onChange={(d) => { const x = d; if (!x || /^\d{4}-\d{2}-\d{2}$/.test(x)) void setRow(r, { clientSince: x || null }); }} />
                    {r.basis !== 'recorded' && r.since && <div className="tiny muted"><SinceCell r={r} /></div>}</>
                  : <SinceCell r={r} />}</td>
                <td className="small">{fill
                  ? <SourcePicker value={view.get('persons', r.id)?.source ?? ''} onChange={(x) => void setRow(r, { source: x || null })} testId="register-source-input" label={`${t('register.col_source')} · ${r.name}`} />
                  : r.source.key === 'unknown' ? <span className="muted">—</span> : sourceText(rt, r.source)}</td>
                <td className="small">{fill && r.source.key === 'referral'
                  ? <FillReferrer r={r} />
                  : r.referredById ? <button className="linkish" onClick={() => navigate(personPath(r.referredById!))}>{r.referredBy}</button> : r.referredBy ?? <span className="muted">—</span>}</td>
                {portfolio && <><td className="num">{r.activePolicies || '—'}</td><td className="num">{r.annualPremium ? i18n.money(r.annualPremium) : '—'}</td></>}
              </tr>))}
            </tbody>
          </table>
        ) : <Empty icon="people">{t('register.empty')}</Empty>}
      </div>
      {reg.rows.some((r) => r.basis === 'first_policy') && <p className="tiny muted">{t('register.note_first_policy')}</p>}
      {reg.rows.some((r) => r.basis === 'added') && <p className="tiny muted">{t('register.note_added')}</p>}
    </div>
  );
}

/** fill mode: type or pick who referred this client; saved when the field is left */
function FillReferrer({ r }: { r: RegisterRow }) {
  const rt = useRuntime();
  const [v, setV] = useState(r.referredBy ?? '');
  const save = () => {
    if (v.trim() === (r.referredBy ?? '')) return;
    void rt.run((w, c) => updatePerson(w, r.id, resolveReferrer(rt, v, r.id), c));
  };
  return <ReferredByInput value={v} onChange={setV} onBlur={save} selfId={r.id} testId="register-referrer-input" />;
}

function SinceCell({ r }: { r: RegisterRow }) {
  const { i18n, t } = useRuntime();
  if (!r.since) return <span className="muted">—</span>;
  const mark = r.basis === 'first_policy' ? '¹' : r.basis === 'added' ? '²' : '';
  return <span title={r.basis ? t(`person.since_basis.${r.basis}`) : undefined} className={r.basis === 'recorded' ? '' : 'muted'}>{i18n.date(r.since)}{mark && <sup>{mark}</sup>}</span>;
}

/** one line for the client card: "Client since 12 Mar 2015 · Source: Referral · referred by Maria" */
export function SinceAndSource({ personId }: { personId: string }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const p = view.get('persons', personId);
  if (!p) return null;
  const since = p.stage === 'client' || p.stage === 'former' ? clientSinceOf(view, p) : { date: null, basis: null };
  const src = sourceInfo(p.source);
  const ref = p.referredByPersonId ? view.get('persons', p.referredByPersonId) : undefined;
  const refName = ref?.displayName ?? p.referredByName ?? src.referrer;
  const effective = src.key === 'unknown' && refName ? { key: 'referral' as const, text: null, group: 'referral' } : src;
  return (<>
    {since.date && <span data-testid="focus-since" title={since.basis ? t(`person.since_basis.${since.basis}`) : undefined}><Icon name="star" />{t('person.client_since_label', { date: i18n.date(since.date) })}{since.basis !== 'recorded' && <span className="muted"> ({t(`person.since_basis.${since.basis}`)})</span>}</span>}
    {effective.key !== 'unknown' && <span data-testid="focus-source"><Icon name="people" />{t('person.source_label', { source: sourceText(rt, effective) })}
      {refName && <> · {ref ? <button className="linkish" onClick={() => navigate(personPath(ref.id))}>{t('person.referred_by_label', { name: refName })}</button> : t('person.referred_by_label', { name: refName })}</>}</span>}
  </>);
}
